import classNames from "classnames";
import { useContext } from "react";
import { useTranslation } from "react-i18next";
import moment from "moment";
import "moment/locale/ar";

import { ContractContext } from "../../../../providers/contract";
import { ESettlementAdvisoryOutcome } from "../../../../setup/api/services/contract/models";
import DataCard from "../OperationMetadata/DataCard";
import { getContractStatusTitle } from "./getContractStatusTitle";
import {
  getPartyFullName,
  getPartyKycTitle,
  getPartyRoleTitle,
  getPartyUserStatusTitle,
} from "./partyMappers";
import styles from "./styles.module.scss";

const formatDate = (date: string) =>
  moment(date).locale("ar").format("DD MMMM YYYY - hh:mm A");

const SettlementAdvisory = () => {
  const { contract } = useContext(ContractContext);
  const { t } = useTranslation();

  const settlementAdvisory = contract?.settlementAdvisory;

  if (!settlementAdvisory) return null;

  const { contextBundle, decision } = settlementAdvisory;
  const {
    contract: contractSnapshot,
    parties,
    risk,
    org,
    exposure,
  } = contextBundle;

  const formatBool = (value: boolean) => (value ? t("lbl_yes") : t("lbl_no"));

  const outcomeLabel = {
    [ESettlementAdvisoryOutcome.AUTO_ALLOW]: t(
      "lbl_settlement_advisory_outcome_auto_allow"
    ),
    [ESettlementAdvisoryOutcome.REVIEW]: t(
      "lbl_settlement_advisory_outcome_review"
    ),
    [ESettlementAdvisoryOutcome.BLOCK]: t(
      "lbl_settlement_advisory_outcome_block"
    ),
  }[decision.outcome];

  return (
    <div className={classNames(styles.container)}>
      <div className={styles.headerContainer}>
        <h2 className={styles.header}>{t("lbl_settlement_advisory")}</h2>
        <span
          className={classNames(styles.outcomeBadge, styles[decision.outcome])}>
          {outcomeLabel}
        </span>
      </div>

      <div className={styles.metaRow}>
        <DataCard
          title={t("lbl_settlement_advisory_decision_id")}
          value={decision.decisionId}
        />
        <DataCard
          title={t("lbl_settlement_advisory_evaluated_at")}
          value={formatDate(decision.evaluatedAt)}
        />
      </div>

      <div className={styles.section}>
        <p className={styles.sectionTitle}>
          {t("lbl_settlement_advisory_contract_snapshot")}
        </p>
        <div className={styles.dataRow}>
          <DataCard
            title={t("lbl_settlement_advisory_status")}
            value={getContractStatusTitle(contractSnapshot.status, t, {
              type: contract?.type,
              senderRole: contract?.senderRole,
            })}
          />
          <DataCard
            title={t("lbl_settlement_advisory_is_settled")}
            value={formatBool(contractSnapshot.isSettled)}
          />
          <DataCard
            title={t("lbl_settlement_advisory_escalations_open")}
            value={formatBool(contractSnapshot.escalationsOpen)}
          />
          <DataCard
            title={t("lbl_settlement_advisory_total_amount")}
            value={String(contractSnapshot.totalAmount)}
          />
          <DataCard
            title={t("lbl_category")}
            value={contractSnapshot.category}
          />
          <DataCard
            title={t("lbl_settlement_advisory_classification")}
            value={contractSnapshot.classification}
          />
          <DataCard
            title={t("lbl_settlement_advisory_parties_count")}
            value={String(contractSnapshot.partiesCount)}
          />
          <DataCard
            title={t("lbl_settlement_advisory_has_arbitrator")}
            value={formatBool(contractSnapshot.hasArbitrator)}
          />
          <DataCard
            title={t("lbl_settlement_advisory_payment_confirmed")}
            value={formatBool(contractSnapshot.paymentConfirmed)}
          />
        </div>
      </div>

      <div className={styles.section}>
        <p className={styles.sectionTitle}>
          {t("lbl_settlement_advisory_org")}
        </p>
        <div className={styles.dataRow}>
          <DataCard
            title={t("lbl_settlement_advisory_org_code")}
            value={org.organizationCode}
          />
          <DataCard
            title={t("lbl_settlement_advisory_opted_in")}
            value={formatBool(org.optedIn)}
          />
          <DataCard
            title={t("lbl_settlement_advisory_trust_tier")}
            value={org.trustTier}
          />
          <DataCard
            title={t("lbl_settlement_advisory_daily_cap_remaining")}
            value={String(org.dailyCapRemaining)}
          />
        </div>
      </div>

      <div className={styles.section}>
        <p className={styles.sectionTitle}>
          {t("lbl_settlement_advisory_risk")}
        </p>
        <div className={styles.comingSoonWrapper}>
          <div className={classNames(styles.dataRow, styles.blurred)}>
            <DataCard
              title={t("lbl_settlement_advisory_fraud_score")}
              value={String(risk.fraudScore)}
            />
            <DataCard
              title={t("lbl_settlement_advisory_fraud_decision")}
              value={risk.fraudDecision}
            />
            <DataCard
              title={t("lbl_settlement_advisory_anomaly_flags")}
              value={
                risk.anomalyFlags.length ? risk.anomalyFlags.join("، ") : "-"
              }
            />
            <DataCard
              title={t("lbl_settlement_advisory_blacklist_hit")}
              value={formatBool(risk.blacklistHit)}
            />
            <DataCard
              title={t("lbl_settlement_advisory_dispute_velocity")}
              value={String(risk.disputeVelocity)}
            />
            <DataCard
              title={t("lbl_settlement_advisory_signal_refs")}
              value={risk.signalRefs.length ? risk.signalRefs.join("، ") : "-"}
            />
          </div>
          <div className={styles.comingSoonOverlay}>
            <span className={styles.comingSoonBadge}>
              <img src="/assets/images/svg/hollow-clock-circle.svg" alt="" />
              {t("lbl_coming_soon")}
            </span>
          </div>
        </div>
      </div>

      <div className={styles.section}>
        <p className={styles.sectionTitle}>
          {t("lbl_settlement_advisory_exposure")}
        </p>
        <div className={styles.comingSoonWrapper}>
          <div className={classNames(styles.dataRow, styles.blurred)}>
            <DataCard
              title={t("lbl_settlement_advisory_rolling_amount")}
              value={String(exposure.rollingAmount)}
            />
            <DataCard
              title={t("lbl_settlement_advisory_rolling_count")}
              value={String(exposure.rollingCount)}
            />
          </div>
          <div className={styles.comingSoonOverlay}>
            <span className={styles.comingSoonBadge}>
              <img src="/assets/images/svg/hollow-clock-circle.svg" alt="" />
              {t("lbl_coming_soon")}
            </span>
          </div>
        </div>
      </div>

      <div className={styles.section}>
        <p className={styles.sectionTitle}>
          {t("lbl_settlement_advisory_parties")}
        </p>
        <div className={styles.partiesContainer}>
          {parties.map((party) => (
            <div className={styles.partyRow} key={party.userId}>
              <DataCard
                title={t("lbl_name")}
                value={
                  getPartyFullName(contract, party.role, party.userId) ||
                  party.userId
                }
              />
              <DataCard
                title={t("lbl_settlement_advisory_party_role")}
                value={getPartyRoleTitle(party.role, t)}
              />
              <DataCard
                title={t("lbl_settlement_advisory_party_kyc")}
                value={getPartyKycTitle(party.kycLevel, t)}
              />
              <DataCard
                title={t("lbl_settlement_advisory_party_status")}
                value={getPartyUserStatusTitle(party.userStatus, t)}
              />
              <DataCard
                title={t("lbl_settlement_advisory_party_manual_withdraw")}
                value={formatBool(party.manualWithDrawCashOut)}
              />
              <DataCard
                title={t("lbl_settlement_advisory_party_credited")}
                value={formatBool(party.credited)}
              />
            </div>
          ))}
        </div>
      </div>
    </div>
  );
};

export default SettlementAdvisory;
